import { useMutation } from '@tanstack/react-query';
import useGetSubscription from './useGetSubscription';
import { createPortalSession } from '../../services/subscriptions';
import { CreatePortalSessionType } from '../../types/subscriptions';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';

interface SubscriptionPortalOptions {
  autoOpen?: boolean;
  onSuccess?: (response: string, type?: CreatePortalSessionType) => void;
  onError?: () => void | Promise<void>;
}

function useSubscriptionPortal({
  autoOpen = true,
  onSuccess,
  onError,
}: SubscriptionPortalOptions = {}) {
  const { user } = useUser<LoadedUser>();
  const { data, isFetching } = useGetSubscription();

  const businessId = user?.business?.id;

  const launchPortalMutation = useMutation({
    mutationFn: async (type?: CreatePortalSessionType) => {
      if (!data?.subscription || isFetching || !businessId) return null;

      const response = await createPortalSession(
        data.subscription.id,
        businessId,
        type,
      );

      return response?.url;
    },
    onSuccess: (url, type) => {
      if (!url) return;

      if (onSuccess) {
        onSuccess(url, type);
      }

      if (autoOpen) {
        window.location.href = url;
      }
    },
    onError,
  });

  return {
    launchPortalMutation,
    isPendingCancel:
      launchPortalMutation.isPending &&
      launchPortalMutation.variables === 'subscription_cancel',
    isPendingUpdatePayment:
      launchPortalMutation.isPending &&
      (launchPortalMutation.variables === 'payment_method_update' ||
        !launchPortalMutation.variables),
    isSuccessCancel:
      launchPortalMutation.isSuccess &&
      launchPortalMutation.variables === 'subscription_cancel',
    isSuccessUpdatePayment:
      launchPortalMutation.isSuccess &&
      (launchPortalMutation.variables === 'payment_method_update' ||
        !launchPortalMutation.variables),
  };
}

export default useSubscriptionPortal;
